import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import AdminNavbar from "../Layout/AdminNavbar";
import ManagerNavbar from "../Layout/ManagerNavbar";
import API_BASE_URL from "../config";

const NAVBAR_HEIGHT = 64;
const HOURS = Array.from({ length: 13 }, (_, i) => i + 8);
const MINUTES = [0, 15, 30, 45];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const EVENT_COLORS = [
    { bg: "#e0f2fe", border: "#38bdf8", text: "#075985", label: "Star of the Week" },
    { bg: "#fef9c3", border: "#facc15", text: "#713f12", label: "Employee of the Month" },
    { bg: "#ecfccb", border: "#84cc16", text: "#365314", label: "Fun Friday" },
    { bg: "#ede9fe", border: "#8b5cf6", text: "#4c1d95", label: "Announcement" },
];

// ── NAVBAR MAP ────────────────────────────────────────────────────────────────
// ── NAVBAR MAP ────────────────────────────────────────────────────────────────
const NAVBAR_MAP = {
    ROLE_ADMIN: AdminNavbar,
    ROLE_MANAGER: ManagerNavbar,
    
    ROLE_USER: EmployeeNavbar,
};

function RoleNavbar() {
    const PRIORITY = ["ROLE_ADMIN", "ROLE_MANAGER", "ROLE_HR", "ROLE_EMPLOYEE"];
    let selectedRole = "ROLE_EMPLOYEE";

    try {
        const rolesRaw = localStorage.getItem("roles");
        const rolesArray = JSON.parse(rolesRaw); // e.g. ["ROLE_ADMIN"] or ["ROLE_MANAGER"]
        selectedRole = PRIORITY.find(p => rolesArray.includes(p)) || "ROLE_EMPLOYEE";
    } catch {
        selectedRole = "ROLE_EMPLOYEE";
    }

    const NavbarComponent = NAVBAR_MAP[selectedRole] || EmployeeNavbar;
    return <NavbarComponent />;
}

// ── AUTH HELPER ───────────────────────────────────────────────────────────────
function authHeaders() {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Time helpers ──────────────────────────────────────────────────────────────
function fmtTime(h, m = 0) {
    const period = h >= 12 ? "PM" : "AM";
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m === 0 ? `${displayH} ${period}` : `${displayH}:${String(m).padStart(2, "0")} ${period}`;
}

function toMins(h, m = 0) { return h * 60 + m; }

// ── AuthImage ─────────────────────────────────────────────────────────────────
function AuthImage({ src, alt, style, fallbackStyle }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [failed, setFailed] = useState(false);
    const prevUrl = useRef(null);

    useEffect(() => {
        if (!src) return;
        if (prevUrl.current) URL.revokeObjectURL(prevUrl.current);
        setBlobUrl(null);
        setFailed(false);

        let cancelled = false;
        fetch(src, { headers: authHeaders() })
            .then(r => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.blob();
            })
            .then(blob => {
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                prevUrl.current = url;
                setBlobUrl(url);
            })
            .catch(() => { if (!cancelled) setFailed(true); });

        return () => { cancelled = true; };
    }, [src]);

    useEffect(() => () => { if (prevUrl.current) URL.revokeObjectURL(prevUrl.current); }, []);

    if (!src || failed) {
        return (
            <div style={{ ...style, ...fallbackStyle, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 20, opacity: 0.3 }}>🖼️</span>
            </div>
        );
    }
    if (!blobUrl) {
        return <div style={{ ...style, background: "#f1f5f9", animation: "pulse 1.5s ease-in-out infinite" }} />;
    }
    return <img src={blobUrl} alt={alt || ""} style={style} />;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function getWeekDates(base) {
    const d = new Date(base);
    const sun = new Date(d);
    sun.setDate(d.getDate() - d.getDay());
    return Array.from({ length: 7 }, (_, i) => { const x = new Date(sun); x.setDate(sun.getDate() + i); return x; });
}
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function parseDate(str) {
    if (!str) return null;
    if (str instanceof Date) return str;
    return new Date(str + "T00:00:00");
}
function toISODate(d) { return d.toISOString().split("T")[0]; }

function normalizeEvent(a) {
    const base = API_BASE_URL.replace(/\/$/, "");
    const url = a.templateUrl ? `${base}/${a.templateUrl.replace(/^\//, "")}` : null;
    return {
        ...a,
        date: parseDate(a.date),
        templatePreview: url,
        colorLabel: EVENT_COLORS[a.colorIdx]?.label ?? "",
        startMinute: a.startMinute ?? 0,
        endMinute: a.endMinute ?? 0,
    };
}

// ── Time Picker ───────────────────────────────────────────────────────────────
function TimePicker({ label, hour, minute, onHourChange, onMinuteChange, filterHours }) {
    const hours = filterHours ? HOURS.filter(filterHours) : HOURS;
    return (
        <div style={tp.wrap}>
            <label style={tp.lbl}>{label}</label>
            <div style={tp.row}>
                <select style={tp.sel} value={hour} onChange={e => onHourChange(+e.target.value)}>
                    {hours.map(h => (
                        <option key={h} value={h}>{h > 12 ? h - 12 : h === 0 ? 12 : h} {h >= 12 ? "PM" : "AM"}</option>
                    ))}
                </select>
                <span style={tp.colon}>:</span>
                <select style={tp.sel} value={minute} onChange={e => onMinuteChange(+e.target.value)}>
                    {MINUTES.map(m => (
                        <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                    ))}
                </select>
            </div>
        </div>
    );
}
const tp = {
    wrap: { flex: 1 },
    lbl: { fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 5 },
    row: { display: "flex", alignItems: "center", gap: 4 },
    sel: { flex: 1, border: "1.5px solid #e2e8f0", borderRadius: 9, padding: "8px 6px", fontSize: 13, color: "#1e293b", outline: "none", background: "#fff", cursor: "pointer", boxSizing: "border-box", fontFamily: "inherit" },
    colon: { fontSize: 16, fontWeight: 700, color: "#94a3b8", flexShrink: 0 },
};

// ── Announcement Banner Overlay ───────────────────────────────────────────────
function AnnouncementBanner({ announcement, onDismiss }) {
    const [visible, setVisible] = useState(true);
    if (!visible || !announcement) return null;
    return (
        <div style={bs.backdrop}>
            <div style={bs.card}>
                <div style={bs.ribbon}>{announcement.colorLabel}</div>
                <button style={bs.closeBtn} onClick={() => { setVisible(false); onDismiss(); }}>✕</button>
                {announcement.templatePreview && (
                    <div style={bs.imgWrap}>
                        <AuthImage src={announcement.templatePreview} alt="Announcement" style={bs.img} />
                    </div>
                )}
                <div style={bs.body}>
                    <div style={bs.badgeRow}>
                        <span style={{ ...bs.badge, background: EVENT_COLORS[announcement.colorIdx].bg, color: EVENT_COLORS[announcement.colorIdx].text }}>{announcement.colorLabel}</span>
                        <span style={bs.timeTag}>🕐 {fmtTime(announcement.startHour, announcement.startMinute)} – {fmtTime(announcement.endHour, announcement.endMinute)}</span>
                    </div>
                    <h2 style={bs.title}>{announcement.title}</h2>
                    {announcement.message && <p style={bs.msg}>{announcement.message}</p>}
                    <div style={bs.dateRow}>📅 {announcement.date?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
                </div>
                <div style={bs.footer}>
                    <button style={bs.dismissBtn} onClick={() => { setVisible(false); onDismiss(); }}>Got it! 👍</button>
                </div>
            </div>
        </div>
    );
}
const bs = {
    backdrop: { position: "fixed", inset: 0, background: "rgba(10,15,30,0.7)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(6px)", animation: "fadeIn 0.3s ease" },
    card: { background: "#fff", borderRadius: 20, width: "100%", maxWidth: 520, boxShadow: "0 32px 80px rgba(0,0,0,0.3)", overflow: "hidden", position: "relative", animation: "slideUp 0.35s cubic-bezier(0.16,1,0.3,1)" },
    ribbon: { position: "absolute", top: 16, left: -28, background: "#1a3c5e", color: "#fff", fontSize: 11, fontWeight: 700, padding: "5px 36px", transform: "rotate(-45deg)", letterSpacing: "0.5px", zIndex: 1 },
    closeBtn: { position: "absolute", top: 12, right: 12, width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.08)", border: "none", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", zIndex: 2 },
    imgWrap: { width: "100%", height: 220, overflow: "hidden", background: "#f1f5f9" },
    img: { width: "100%", height: "100%", objectFit: "cover" },
    body: { padding: "20px 24px 16px" },
    badgeRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12 },
    badge: { borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700 },
    timeTag: { fontSize: 12, color: "#94a3b8", fontWeight: 500 },
    title: { fontSize: 22, fontWeight: 800, color: "#1a3c5e", margin: "0 0 8px", lineHeight: 1.2 },
    msg: { fontSize: 14, color: "#475569", lineHeight: 1.6, margin: "0 0 12px" },
    dateRow: { fontSize: 12, color: "#94a3b8", fontWeight: 500 },
    footer: { padding: "12px 24px 20px", background: "#f8fafc", display: "flex", justifyContent: "flex-end" },
    dismissBtn: { background: "#1a3c5e", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
};

// ── Scheduled Announcement Item ───────────────────────────────────────────────
function ScheduledCard({ item, onDelete, onPreview, onEdit }) {
    const [timeLeft, setTimeLeft] = useState("");
    useEffect(() => {
        const tick = () => {
            const now = new Date();
            const target = new Date(item.date);
            target.setHours(item.startHour, item.startMinute ?? 0, 0, 0);
            const diff = target - now;
            if (diff <= 0) { setTimeLeft("🔴 Broadcasting now"); return; }
            const h = Math.floor(diff / 3600000), m = Math.floor((diff % 3600000) / 60000), s2 = Math.floor((diff % 60000) / 1000);
            setTimeLeft(`⏱ ${h}h ${m}m ${s2}s`);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [item]);
    const c = EVENT_COLORS[item.colorIdx];
    return (
        <div style={{ ...sc.card, borderLeft: `4px solid ${c.border}`, background: c.bg + "44" }}>
            {item.templatePreview && (
                <AuthImage src={item.templatePreview} alt="" style={sc.thumb} />
            )}
            <div style={sc.info}>
                <div style={{ ...sc.label, color: c.text }}>{c.label}</div>
                <div style={sc.title}>{item.title}</div>
                <div style={sc.meta}>
                    {item.date?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    {" · "}
                    {fmtTime(item.startHour, item.startMinute ?? 0)}–{fmtTime(item.endHour, item.endMinute ?? 0)}
                </div>
                <div style={sc.countdown}>{timeLeft}</div>
            </div>
            <div style={sc.actions}>
                <button style={sc.prevBtn} onClick={() => onPreview(item)}>👁 Preview</button>
                <button style={sc.editBtn} onClick={() => onEdit(item)}>✏️ Edit</button>
                <button style={sc.delBtn} onClick={() => onDelete(item.id)}>🗑</button>
            </div>
        </div>
    );
}
const sc = {
    card: { display: "flex", alignItems: "center", gap: 12, borderRadius: 12, padding: "12px 14px", marginBottom: 10, transition: "all 0.2s" },
    thumb: { width: 52, height: 52, borderRadius: 8, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(0,0,0,0.08)" },
    info: { flex: 1, minWidth: 0 },
    label: { fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 },
    title: { fontSize: 13, fontWeight: 700, color: "#1a3c5e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    meta: { fontSize: 11, color: "#64748b", marginTop: 1 },
    countdown: { fontSize: 11, color: "#475569", fontWeight: 600, marginTop: 2 },
    actions: { display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 },
    prevBtn: { fontSize: 11, fontWeight: 600, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 7, padding: "4px 8px", cursor: "pointer", color: "#475569" },
    editBtn: { fontSize: 11, fontWeight: 600, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, padding: "4px 8px", cursor: "pointer", color: "#1d4ed8" },
    delBtn: { fontSize: 13, background: "#fee2e2", border: "none", borderRadius: 7, padding: "4px 8px", cursor: "pointer" },
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function Announcement() {
    const today = new Date();

    const [template, setTemplate] = useState(null);
    const [templatePreview, setTemplatePreview] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef(null);
    const [hasAccess, setHasAccess] = useState(null);

    const dismissedIds = useRef(
        new Set(JSON.parse(localStorage.getItem("dismissedAnnouncements") || "[]"))
    );

    const [baseDate, setBaseDate] = useState(today);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalDay, setModalDay] = useState(null);
    const [form, setForm] = useState({
        title: "",
        message: "",
        startHour: 9,
        startMinute: 0,
        endHour: 10,
        endMinute: 0,
        colorIdx: 3,
    });

    const [previewAnnouncement, setPreviewAnnouncement] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);

    const weekDates = getWeekDates(baseDate);

    // ── GET /week ─────────────────────────────────────────────────────────────
    const loadWeek = useCallback(async () => {
        const from = weekDates[0];
        const to = weekDates[6];
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/announcements/week`, {
                params: { from: toISODate(from), to: toISODate(to) },
                headers: authHeaders(),
            });
            setEvents(res.data.map(normalizeEvent));
        } catch (err) {
            if (err.response?.status === 401) toast.error("Session expired. Please log in again.");
            else toast.error("Failed to load announcements.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [baseDate]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadWeek(); }, [loadWeek]);

    useEffect(() => {
        const checkPermission = async () => {
            try {
                const token = localStorage.getItem("token");
                const employeeId = localStorage.getItem("employeeId");
                const res = await axios.get(
                    `${API_BASE_URL}/api/permissions/${employeeId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const permissions = res.data.pages;
                setHasAccess(permissions?.announcement === true);
            } catch (err) {
                console.error("Permission error:", err);
                setHasAccess(false);
            }
        };
        checkPermission();
    }, []);

    // Template handlers
    const handleFile = (file) => {
        if (!file || !file.type.startsWith("image/")) { toast.error("Please select an image."); return; }
        setTemplate(file);
        const r = new FileReader();
        r.onload = (e) => setTemplatePreview(e.target.result);
        r.readAsDataURL(file);
    };
    const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); };
    const removeTemplate = () => { setTemplate(null); setTemplatePreview(null); if (fileRef.current) fileRef.current.value = ""; };

    const prevWeek = () => { const d = new Date(baseDate); d.setDate(d.getDate() - 7); setBaseDate(d); };
    const nextWeek = () => { const d = new Date(baseDate); d.setDate(d.getDate() + 7); setBaseDate(d); };

    const openModal = (day, hour) => {
        setEditingEvent(null);
        setModalDay(new Date(day));
        setForm({
            title: "",
            message: "",
            date: toISODate(new Date(day)),
            startHour: hour,
            startMinute: 0,
            endHour: Math.min(hour + 1, 20),
            endMinute: 0,
            colorIdx: 3,
        });
        setShowModal(true);
    };

    const openEditModal = (evt) => {
        setEditingEvent(evt);
        setModalDay(new Date(evt.date));
        setForm({
            title: evt.title,
            message: evt.message || "",
            date: toISODate(new Date(evt.date)),
            startHour: evt.startHour,
            startMinute: evt.startMinute ?? 0,
            endHour: evt.endHour,
            endMinute: evt.endMinute ?? 0,
            colorIdx: evt.colorIdx,
        });
        setTemplatePreview(evt.templatePreview || null);
        setTemplate(null);
        setShowModal(true);
    };

    // ── POST (create) / PUT (edit) ────────────────────────────────────────────
    const saveEvent = async () => {
        if (!form.title.trim()) { toast.error("Title is required."); return; }
        if (!form.date) { toast.error("Please select a broadcast date."); return; }

        const startTotal = toMins(form.startHour, form.startMinute);
        const endTotal = toMins(form.endHour, form.endMinute);
        if (startTotal >= endTotal) { toast.error("End time must be after start time."); return; }

        const dto = {
            title: form.title,
            message: form.message,
            date: form.date,
            startHour: form.startHour,
            startMinute: form.startMinute,
            endHour: form.endHour,
            endMinute: form.endMinute,
            colorIdx: form.colorIdx,
        };

        const formData = new FormData();
        formData.append("data", new Blob([JSON.stringify(dto)], { type: "application/json" }));
        if (template) formData.append("template", template);

        setSaving(true);
        try {
            if (editingEvent) {
                const res = await axios.put(
                    `${API_BASE_URL}/api/announcements/${editingEvent.id}`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data", ...authHeaders() } }
                );
                setEvents(prev => prev.map(e => e.id === editingEvent.id ? normalizeEvent(res.data) : e));
                toast.success("✏️ Announcement updated!");
            } else {
                const res = await axios.post(`${API_BASE_URL}/api/announcements`, formData, {
                    headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
                });
                setEvents(prev => [...prev, normalizeEvent(res.data)]);
                toast.success(`📅 Announcement scheduled for ${fmtTime(form.startHour, form.startMinute)}!`);
            }
            setShowModal(false);
            setEditingEvent(null);
            removeTemplate();
        } catch (err) {
            if (err.response?.status === 401) toast.error("Session expired. Please log in again.");
            else toast.error(err.response?.data || "Failed to save announcement.");
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    // ── DELETE ────────────────────────────────────────────────────────────────
    const deleteEvent = async (id) => {
        try {
            await axios.delete(`${API_BASE_URL}/api/announcements/${id}`, { headers: authHeaders() });
            setEvents(prev => prev.filter(x => x.id !== id));
            toast.success("Announcement deleted.");
        } catch (err) {
            if (err.response?.status === 401) toast.error("Session expired. Please log in again.");
            else toast.error("Failed to delete announcement.");
            console.error(err);
        }
    };

    const weekEvts = events.filter(e => weekDates.some(d => sameDay(d, e.date)));
    const todayEvts = events.filter(e => sameDay(e.date, today));
    const totalMinsAll = weekEvts.reduce((a, e) => a + (toMins(e.endHour, e.endMinute) - toMins(e.startHour, e.startMinute)), 0);
    const totalHoursDisplay = (totalMinsAll / 60).toFixed(1).replace(".0", "");

    const monthLabel = (() => {
        const f = weekDates[0], l = weekDates[6];
        return f.getMonth() === l.getMonth()
            ? `${MONTHS[f.getMonth()]} ${f.getFullYear()}`
            : `${MONTHS[f.getMonth()].slice(0, 3)} – ${MONTHS[l.getMonth()].slice(0, 3)} ${l.getFullYear()}`;
    })();

    if (hasAccess === null) {
        return <div style={{ padding: 20 }}>Loading...</div>;
    }

    if (!hasAccess) {
        return (
            <div className="text-center mt-5">
                <h2>🚫 Access Denied</h2>
                <p>You do not have permission to access Announcement page.</p>
            </div>
        );
    }

    return (
        <div style={s.root}>
            <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes slideUp{from{opacity:0;transform:translateY(30px) scale(0.96)}to{opacity:1;transform:none}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
        .side-sc::-webkit-scrollbar{width:4px}.side-sc::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:99px}.side-sc{scrollbar-width:thin;scrollbar-color:#cbd5e1 transparent}
        .cal-sc::-webkit-scrollbar{width:5px}.cal-sc::-webkit-scrollbar-thumb{background:#c7d2e8;border-radius:99px}.cal-sc{scrollbar-width:thin;scrollbar-color:#c7d2e8 transparent}
        .evt-chip:hover .evt-del{opacity:1!important}.evt-chip:hover .evt-edit{opacity:1!important}.day-col:hover .add-btn{opacity:1!important}
        .rm-btn:hover{background:#fee2e2!important;color:#dc2626!important}
        .dz.drag{border-color:#3b82f6!important;background:#eff6ff!important}
        .nbtn:hover{background:#f0f7ff!important}.tbtn:hover{background:#e8f0fe!important}
        .time-sel:focus{border-color:#3b82f6!important;box-shadow:0 0 0 3px rgba(59,130,246,0.12)!important}
      `}</style>

            {previewAnnouncement && (
                <AnnouncementBanner
                    announcement={previewAnnouncement}
                    onDismiss={() => setPreviewAnnouncement(null)}
                />
            )}

            {/* ── DYNAMIC NAVBAR ── */}
            <div style={s.nav}><RoleNavbar /></div>

            <div style={s.body}>
                {/* ═══ LEFT SIDEBAR ═══ */}
                <div style={s.sidebar}>
                    <div className="side-sc" style={s.sideInner}>
                        <div style={s.sideHead}>📣 Announcements</div>
                        <div style={s.sideSub}>Upload template · Schedule · Broadcast</div>
                        <div style={s.hr} />

                        <div style={s.secLabel}>🖼️ Announcement Template</div>
                        <div style={s.dzHelpTxt}>Upload a banner/poster image that will be shown to all employees at the scheduled time.</div>

                        <div className={`dz${dragOver ? " drag" : ""}`} style={s.dz}
                            onClick={() => fileRef.current?.click()}
                            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}>
                            {templatePreview
                                ? <div style={s.pw}><img src={templatePreview} alt="" style={s.pi} /><div style={s.povLabel}>🔄 Click to replace</div></div>
                                : <div style={s.dzInner}><div style={{ fontSize: 32, marginBottom: 6 }}>🗞️</div><div style={s.dzTxt}>Drag & drop your announcement banner</div><div style={s.dzHint}>JPG · PNG · WebP · Max 5 MB</div></div>
                            }
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />

                        {template && (
                            <div style={s.fileRow}>
                                <span style={s.fname}>✅ {template.name}</span>
                                <button className="rm-btn" style={s.rmBtn} onClick={removeTemplate}>✕</button>
                            </div>
                        )}
                        {templatePreview && (
                            <div style={s.templateNotice}><span style={{ fontSize: 13 }}>📌</span><span>This template will be attached to your next scheduled announcement.</span></div>
                        )}

                        <div style={s.tips}>
                            {["Recommended: 16:9 or square crop", "Min 800×400 px for crisp display", "Use bold text visible from a distance"].map((t, i) => (
                                <div key={i} style={s.tipRow}><span style={s.tipDot} />{t}</div>
                            ))}
                        </div>
                        <div style={s.hr} />

                        <div style={s.secLabel}>📊 This Week</div>
                        <div style={s.statRow}>
                            <div style={s.statBox}><div style={s.statN}>{weekEvts.length}</div><div style={s.statL}>Scheduled</div></div>
                            <div style={s.statBox}><div style={s.statN}>{totalHoursDisplay}h</div><div style={s.statL}>Duration</div></div>
                        </div>
                        <div style={s.hr} />

                        <div style={s.secLabel}>🗓 Upcoming Announcements</div>
                        {events.length === 0
                            ? <div style={s.noEvt}>No announcements scheduled</div>
                            : events.sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 5).map(e => (
                                <ScheduledCard key={e.id} item={e} onDelete={deleteEvent} onPreview={item => setPreviewAnnouncement(item)} onEdit={openEditModal} />
                            ))
                        }
                    </div>
                </div>

                {/* ═══ RIGHT CALENDAR PANEL ═══ */}
                <div style={s.panel}>
                    <div style={s.toolbar}>
                        <div style={s.toolL}>
                            <span style={{ fontSize: 22 }}>🗓</span>
                            <div>
                                <div style={s.calTitle}>Announcement Scheduler</div>
                                <div style={s.calSub}>{monthLabel} · {loading ? "Loading…" : "Click any cell to schedule"}</div>
                            </div>
                        </div>
                        <div style={s.toolR}>
                            <button className="tbtn" style={s.todayBtn} onClick={() => setBaseDate(today)}>Today</button>
                            <button className="nbtn" style={s.navBtn} onClick={prevWeek}>‹</button>
                            <button className="nbtn" style={s.navBtn} onClick={nextWeek}>›</button>
                        </div>
                    </div>

                    {todayEvts.length > 0 && (
                        <div style={s.broadcastBar}>
                            <div style={s.broadcastDot} />
                            <span><b>{todayEvts.length}</b> announcement{todayEvts.length > 1 ? "s" : ""} scheduled for today</span>
                            <div style={s.broadcastBadges}>
                                {todayEvts.map(e => (
                                    <span key={e.id} style={{ ...s.broadcastChip, background: EVENT_COLORS[e.colorIdx].bg, color: EVENT_COLORS[e.colorIdx].text }}>
                                        {fmtTime(e.startHour, e.startMinute)} · {e.title}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={s.dayHdr}>
                        <div style={s.gutter} />
                        {weekDates.map((d, i) => {
                            const isTd = sameDay(d, today);
                            return (
                                <div key={i} style={s.dayHead}>
                                    <div style={{ ...s.dName, color: isTd ? "#3b82f6" : "#94a3b8" }}>{DAYS[i]}</div>
                                    <div style={{ ...s.dNum, background: isTd ? "#3b82f6" : "transparent", color: isTd ? "#fff" : "#1e293b" }}>{d.getDate()}</div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="cal-sc" style={s.grid}>
                        {HOURS.map(hour => (
                            <div key={hour} style={s.hourRow}>
                                <div style={s.tLabel}>{fmtTime(hour)}</div>
                                {weekDates.map((d, di) => {
                                    const cellEvts = events.filter(e => sameDay(e.date, d) && e.startHour === hour);
                                    return (
                                        <div key={di} className="day-col"
                                            style={{ ...s.cell, background: sameDay(d, today) ? "#f8fbff" : "transparent" }}
                                            onDoubleClick={() => openModal(d, hour)}>
                                            <button className="add-btn" style={s.addBtn} onClick={() => openModal(d, hour)}>+</button>
                                            {cellEvts.map(evt => {
                                                const c = EVENT_COLORS[evt.colorIdx];
                                                const durationMins = toMins(evt.endHour, evt.endMinute) - toMins(evt.startHour, evt.startMinute);
                                                const chipH = Math.max((durationMins / 60) * 58 - 6, 22);
                                                return (
                                                    <div key={evt.id} className="evt-chip" style={{ ...s.chip, background: c.bg, border: `1.5px solid ${c.border}`, color: c.text, height: `${chipH}px` }}>
                                                        {evt.templatePreview && (
                                                            <AuthImage src={evt.templatePreview} alt="" style={s.chipThumb} />
                                                        )}
                                                        <div style={s.chipTitle}>{evt.title}</div>
                                                        <div style={s.chipTime}>{fmtTime(evt.startHour, evt.startMinute)}–{fmtTime(evt.endHour, evt.endMinute)}</div>
                                                        <div style={s.broadcastIcon}>📣</div>
                                                        <div style={s.chipBtns}>
                                                            <button className="evt-edit" style={s.evtEdit} onClick={e2 => { e2.stopPropagation(); openEditModal(evt); }}>✏️</button>
                                                            <button className="evt-del" style={s.evtDel} onClick={e2 => { e2.stopPropagation(); deleteEvent(evt.id); }}>✕</button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                    <div style={s.hint}>Double-click or press <b>+</b> to schedule an announcement · It auto-broadcasts to all employees at the set time</div>
                </div>
            </div>

            {/* ═══ MODAL ═══ */}
            {showModal && (
                <div style={s.backdrop} onClick={() => { setShowModal(false); setEditingEvent(null); removeTemplate(); }}>
                    <div style={s.modal} onClick={e => e.stopPropagation()}>
                        <div style={s.mHeader}>
                            <div style={s.mTitle}>{editingEvent ? "✏️ Edit Announcement" : "📣 Schedule Announcement"}</div>
                            <div style={s.mSub}>
                                {form.date
                                    ? parseDate(form.date)?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
                                    : "Pick a date below"
                                }
                            </div>
                        </div>

                        {editingEvent
                            ? <div style={s.editModeBanner}>✏️ Editing existing announcement — change any fields below and save.</div>
                            : null
                        }

                        {templatePreview
                            ? <div style={s.mThumbRow}>
                                <img src={typeof templatePreview === "string" && templatePreview.startsWith("http") ? undefined : templatePreview}
                                    style={s.mThumb}
                                    alt=""
                                    onError={e2 => { e2.target.style.display = "none"; }}
                                />
                                {editingEvent && !template
                                    ? <div><div style={{ fontSize: 12, fontWeight: 700, color: "#1a3c5e" }}>Existing template</div><div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Upload a new image below to replace it</div></div>
                                    : <div><div style={{ fontSize: 12, fontWeight: 700, color: "#1a3c5e" }}>Template attached</div><div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>This banner will be shown to all employees</div></div>
                                }
                            </div>
                            : <div style={s.noTemplateWarn}>⚠️ No template uploaded. Upload a banner in the sidebar to attach it to this announcement.</div>
                        }

                        <div style={s.fg}>
                            <label style={s.lbl}>Announcement Title *</label>
                            <input style={s.inp} placeholder="e.g. Employee of the Month – April 2026" autoFocus value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                        </div>

                        <div style={s.fg}>
                            <label style={s.lbl}>📅 Broadcast Date *</label>
                            <input
                                type="date"
                                style={{ ...s.inp, colorScheme: "light" }}
                                value={form.date || ""}
                                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                            />
                            {form.date && (
                                <div style={s.dateParsed}>
                                    {parseDate(form.date)?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                                </div>
                            )}
                        </div>
                        <div style={s.fg}>
                            <label style={s.lbl}>Message (optional)</label>
                            <textarea style={{ ...s.inp, minHeight: 72, resize: "vertical", lineHeight: 1.5 }} placeholder="Add a short message…" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
                        </div>

                        <div style={s.fRow}>
                            <TimePicker
                                label="Broadcast Start"
                                hour={form.startHour}
                                minute={form.startMinute}
                                onHourChange={h => setForm(f => ({ ...f, startHour: h }))}
                                onMinuteChange={m => setForm(f => ({ ...f, startMinute: m }))}
                            />
                            <TimePicker
                                label="Broadcast End"
                                hour={form.endHour}
                                minute={form.endMinute}
                                onHourChange={h => setForm(f => ({ ...f, endHour: h }))}
                                onMinuteChange={m => setForm(f => ({ ...f, endMinute: m }))}
                            />
                        </div>

                        {(() => {
                            const diff = toMins(form.endHour, form.endMinute) - toMins(form.startHour, form.startMinute);
                            if (diff <= 0) return (
                                <div style={s.durationWarn}>⚠️ End time must be after start time</div>
                            );
                            const h = Math.floor(diff / 60), m = diff % 60;
                            const label = [h && `${h}h`, m && `${m}m`].filter(Boolean).join(" ");
                            return (
                                <div style={s.durationPill}>
                                    🕐 {fmtTime(form.startHour, form.startMinute)} → {fmtTime(form.endHour, form.endMinute)}
                                    <span style={s.durationBadge}>{label}</span>
                                </div>
                            );
                        })()}

                        <div style={s.fg}>
                            <label style={s.lbl}>Announcement Type</label>
                            <div style={s.colorRow}>
                                {EVENT_COLORS.map((c, i) => (
                                    <div key={i} style={{ ...s.colorOpt, background: c.bg, color: c.text, border: `2px solid ${form.colorIdx === i ? c.border : "transparent"}`, boxShadow: form.colorIdx === i ? `0 0 0 2px ${c.border}40` : "none" }}
                                        onClick={() => setForm(f => ({ ...f, colorIdx: i }))}>{c.label}</div>
                                ))}
                            </div>
                        </div>
                        <div style={s.broadcastNote}>
                            🔔 This announcement will automatically appear as a full-screen popup on all employee dashboards at <b>{fmtTime(form.startHour, form.startMinute)}</b>.
                        </div>
                        <div style={s.mFooter}>
                            <button style={s.cancelBtn} onClick={() => { setShowModal(false); setEditingEvent(null); removeTemplate(); }} disabled={saving}>Cancel</button>
                            <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1, background: editingEvent ? "#1e40af" : "#1a3c5e" }} onClick={saveEvent} disabled={saving}>
                                {saving ? "⏳ Saving…" : editingEvent ? "💾 Save Changes" : "📅 Schedule Broadcast"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const s = {
    root: { height: "100vh", overflow: "hidden", display: "flex", flexDirection: "column", background: "linear-gradient(135deg,#f0f4ff 0%,#e8f0fe 100%)", fontFamily: "'Segoe UI',system-ui,sans-serif" },
    nav: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, height: NAVBAR_HEIGHT, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
    body: { marginTop: NAVBAR_HEIGHT + 40, height: `calc(100vh - ${NAVBAR_HEIGHT}px)`, display: "flex", gap: 16, overflow: "hidden", padding: 16 },
    sidebar: { width: 280, flexShrink: 0, height: "100%", background: "#fff", borderRadius: 16, boxShadow: "0 2px 16px rgba(26,60,94,0.07)", display: "flex", flexDirection: "column", overflow: "hidden" },
    sideInner: { flex: 1, overflowY: "auto", overflowX: "hidden", padding: "20px 18px", minHeight: 0 },
    sideHead: { fontSize: 15, fontWeight: 800, color: "#1a3c5e", letterSpacing: "-0.3px" },
    sideSub: { fontSize: 11, color: "#94a3b8", marginTop: 2 },
    hr: { height: 1, background: "#f1f5f9", margin: "14px 0" },
    secLabel: { fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 },
    dzHelpTxt: { fontSize: 11, color: "#94a3b8", marginBottom: 10, lineHeight: 1.5 },
    dz: { border: "2px dashed #cbd5e1", borderRadius: 12, cursor: "pointer", transition: "all 0.2s", marginBottom: 10, overflow: "hidden", minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa" },
    dzInner: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: 14 },
    dzTxt: { fontSize: 12, color: "#475569", fontWeight: 500, textAlign: "center" },
    dzHint: { fontSize: 11, color: "#94a3b8" },
    pw: { width: "100%", position: "relative" },
    pi: { width: "100%", height: 120, objectFit: "cover", display: "block" },
    povLabel: { position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: 11, textAlign: "center", padding: "5px", fontWeight: 600 },
    fileRow: { display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", borderRadius: 7, padding: "5px 10px", marginBottom: 8, gap: 6 },
    fname: { fontSize: 11, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 },
    rmBtn: { fontSize: 11, color: "#94a3b8", background: "#f1f5f9", border: "none", borderRadius: 6, padding: "2px 7px", cursor: "pointer", flexShrink: 0, transition: "all 0.15s" },
    templateNotice: { background: "#eff6ff", borderRadius: 8, padding: "8px 10px", fontSize: 11, color: "#1d4ed8", display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 10, lineHeight: 1.5 },
    tips: { background: "#f8fafc", borderRadius: 8, padding: "9px 11px", display: "flex", flexDirection: "column", gap: 5 },
    tipRow: { display: "flex", alignItems: "center", gap: 7, fontSize: 11, color: "#64748b" },
    tipDot: { width: 4, height: 4, borderRadius: "50%", background: "#3b82f6", flexShrink: 0 },
    statRow: { display: "flex", gap: 8, marginBottom: 10 },
    statBox: { flex: 1, background: "#f8fafc", borderRadius: 10, padding: "10px 8px", textAlign: "center" },
    statN: { fontSize: 20, fontWeight: 800, color: "#1a3c5e", lineHeight: 1 },
    statL: { fontSize: 10, color: "#94a3b8", marginTop: 3, fontWeight: 600 },
    noEvt: { fontSize: 12, color: "#cbd5e1", textAlign: "center", padding: "10px 0", fontStyle: "italic" },
    panel: { flex: 1, minWidth: 0, height: "100%", background: "#fff", borderRadius: 16, boxShadow: "0 2px 16px rgba(26,60,94,0.07)", display: "flex", flexDirection: "column", overflow: "hidden" },
    toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #f1f5f9", flexShrink: 0, flexWrap: "wrap", gap: 10 },
    toolL: { display: "flex", alignItems: "center", gap: 10 },
    toolR: { display: "flex", gap: 6 },
    calTitle: { fontSize: 15, fontWeight: 700, color: "#1a3c5e" },
    calSub: { fontSize: 11, color: "#94a3b8", marginTop: 1 },
    todayBtn: { border: "1.5px solid #1a3c5e", background: "transparent", color: "#1a3c5e", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "background 0.15s" },
    navBtn: { border: "1px solid #e2e8f0", background: "#fff", color: "#475569", borderRadius: 8, width: 32, height: 32, fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" },
    broadcastBar: { background: "#fffbeb", borderBottom: "1px solid #fde68a", padding: "8px 20px", fontSize: 12, color: "#92400e", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, flexWrap: "wrap" },
    broadcastDot: { width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", animation: "pulse 1.5s ease-in-out infinite", flexShrink: 0 },
    broadcastBadges: { display: "flex", gap: 6, flexWrap: "wrap" },
    broadcastChip: { borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 },
    dayHdr: { display: "grid", gridTemplateColumns: "60px repeat(7,1fr)", borderBottom: "1px solid #f1f5f9", flexShrink: 0 },
    gutter: { width: 60 },
    dayHead: { display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 0", gap: 4 },
    dName: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" },
    dNum: { width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, transition: "all 0.15s" },
    grid: { flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 },
    hourRow: { display: "grid", gridTemplateColumns: "60px repeat(7,1fr)", minHeight: 58, borderBottom: "1px solid #f8fafc" },
    tLabel: { fontSize: 10, color: "#94a3b8", textAlign: "right", padding: "5px 8px 0 0", fontWeight: 600 },
    cell: { position: "relative", borderLeft: "1px solid #f1f5f9", minHeight: 58, transition: "background 0.15s" },
    addBtn: { position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: "50%", background: "#3b82f6", color: "#fff", border: "none", fontSize: 14, cursor: "pointer", opacity: 0, transition: "opacity 0.15s", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3, padding: 0, lineHeight: 1 },
    chip: { position: "absolute", top: 2, left: 3, right: 3, borderRadius: 6, padding: "3px 5px", overflow: "hidden", zIndex: 2 },
    chipThumb: { width: "100%", height: 28, objectFit: "cover", borderRadius: 4, marginBottom: 2 },
    chipTitle: { fontSize: 11, fontWeight: 700, lineHeight: 1.3, marginBottom: 1 },
    chipTime: { fontSize: 10, opacity: 0.8 },
    broadcastIcon: { fontSize: 9, position: "absolute", bottom: 3, right: 3 },
    chipBtns: { position: "absolute", top: 3, right: 3, display: "flex", gap: 2 },
    evtEdit: { width: 15, height: 15, borderRadius: "50%", background: "rgba(59,130,246,0.18)", color: "#1d4ed8", border: "none", fontSize: 8, cursor: "pointer", opacity: 0, transition: "opacity 0.15s", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
    evtDel: { width: 15, height: 15, borderRadius: "50%", background: "rgba(0,0,0,0.12)", color: "inherit", border: "none", fontSize: 8, cursor: "pointer", opacity: 0, transition: "opacity 0.15s", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
    hint: { fontSize: 11, color: "#cbd5e1", textAlign: "center", padding: "8px", flexShrink: 0 },
    backdrop: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
    modal: { background: "#fff", borderRadius: 16, padding: 28, width: "100%", maxWidth: 500, boxShadow: "0 24px 60px rgba(0,0,0,0.18)", animation: "fadeUp 0.2s ease", maxHeight: "90vh", overflowY: "auto" },
    mHeader: { marginBottom: 20 },
    mTitle: { fontSize: 18, fontWeight: 800, color: "#1a3c5e" },
    mSub: { fontSize: 12, color: "#94a3b8", marginTop: 3 },
    mThumbRow: { display: "flex", gap: 12, alignItems: "center", background: "#f8fafc", borderRadius: 10, padding: "10px 12px", marginBottom: 16 },
    mThumb: { width: 64, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 },
    noTemplateWarn: { background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#92400e", marginBottom: 14 },
    editModeBanner: { background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#1e40af", marginBottom: 14, fontWeight: 600 },
    dateParsed: { fontSize: 11, color: "#3b82f6", fontWeight: 600, marginTop: 5, paddingLeft: 2 },
    fg: { marginBottom: 14 },
    fRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 },
    lbl: { fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 5 },
    inp: { width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 9, padding: "8px 11px", fontSize: 13, color: "#1e293b", outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
    sel: { width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 9, padding: "8px 11px", fontSize: 13, color: "#1e293b", outline: "none", background: "#fff", cursor: "pointer", boxSizing: "border-box", fontFamily: "inherit" },
    durationPill: { display: "flex", alignItems: "center", gap: 8, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "7px 12px", fontSize: 12, color: "#166534", marginBottom: 14, fontWeight: 600 },
    durationBadge: { marginLeft: "auto", background: "#166534", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 },
    durationWarn: { background: "#fff1f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "7px 12px", fontSize: 12, color: "#b91c1c", marginBottom: 14, fontWeight: 600 },
    colorRow: { display: "flex", gap: 7, flexWrap: "wrap" },
    colorOpt: { borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" },
    broadcastNote: { background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#166534", marginBottom: 14, lineHeight: 1.5 },
    mFooter: { display: "flex", gap: 10, marginTop: 8 },
    cancelBtn: { flex: 1, border: "1.5px solid #e2e8f0", background: "transparent", color: "#64748b", borderRadius: 9, padding: "9px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
    saveBtn: { flex: 1, background: "#1a3c5e", color: "#fff", border: "none", borderRadius: 9, padding: "9px", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "opacity 0.2s" },
};